/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FC } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from "@fullcalendar/interaction"
import RideApi from 'app/modules/tp/rides/_requests';
import { Ride } from 'app/modules/tp/rides/_models';
import { DatesSetArg, EventClickArg, EventSourceInput } from '@fullcalendar/core';
import ModalEntity, { Actions } from "app/modules/tp/components/Modal/ModalEntity";
import { emptyService, Service } from 'app/modules/tp/services/_models';
import ServiceFormBuilder from 'app/modules/tp/services/builders/ServiceFormBuilder';
import * as ServiceApi from 'app/modules/tp/services/_requests';
import { ToolbarWrapper } from '_metronic/layout/components/toolbar';
import { DATE_TIME_FORMAT } from 'app/modules/tp/rides/_constants';
import { getStatusColor } from 'app/modules/tp/rides/utility';
import { Filters, IErrorResponse, IValidationErrorResponse, Response, } from "app/modules/tp/_apiResponse";
import { createErrorMessage, createSuccessMessage, createWarningMessage } from "app/partials/Alert";
import { QueryParams, ResourceAction } from "app/infrastructure/repository";
import ValidationError from "app/partials/form/ValidationError";
import Select from 'react-select';
import { Driver } from 'app/modules/tp/drivers/_models';
import { Mean } from 'app/modules/tp/means/_models';


type DateRange = {
    startDate: string,
    endDate: string
}

const RidesCalendarPage: FC = () => {
    const intl = useIntl();
    const [rides, setRides] = React.useState<Ride[]>([]);
    const [dateRange, setDateRange] = React.useState<DateRange | null>();

    const [filteredDrivers, setFilteredDrivers] = React.useState<number[]>([]);
    const [filteredMeans, setFilteredMeans] = React.useState<number[]>([]);

    const [showModal, setShowModal] = React.useState<boolean>(false);
    const [modalEntity, setModalEntity] = React.useState<Service>(emptyService);
    const [modalError, setModalError] = React.useState<JSX.Element>(<></>);
    const [modalAlertMessage, setModalAlertMessage] = React.useState<JSX.Element>(<></>);
    const [alertMessage, setAlertMessage] = React.useState<JSX.Element>(<></>);
    const entityApi = ServiceApi.default;

    const drivers = React.useMemo(() => (
        Array.from(
            (new Map(
                rides?.map((ride: Ride) => [ride?.driver?.id, ride?.driver])
            )).values()
        ).filter((driver) => !!driver)
    ), [rides]);

    const means = React.useMemo(() => (
        Array.from(
            (new Map(
                rides?.map((ride: Ride) => [ride?.mean?.id, ride?.mean])
            )).values()
        ).filter((mean) => !!mean)
    ), [rides]);

    const actionApi = async (action: ResourceAction, entity: Service, callback?: ((e: Service) => void)) => {
        let res: Response<Service> | undefined;
        switch (action) {
            case "create":
                res = await entityApi.create(entity)
                break;
            case "update":
                res = await entityApi.update(entity.id, entity)
                break;
            case "delete":
                res = await entityApi.delete(entity.id)
                break;
            default:
                break;
        }
        if (!(res?.success)) {
            return res;
        }
        callback && res && callback(res?.data as Service);
        return res;
    }

    const actionAndUpdateView = async (action: ResourceAction | "save", entity: Service) => {
        if (action === "save") {
            action = (entity.id ? "update" : "create");
        }
        const res = await actionApi(action, entity)
        if (res?.success === false) {
            if ((res as IErrorResponse)?.type === "foreign_relation") {
                return setModalError(createWarningMessage("Oops!", "Non è possibile eliminare la risorsa perché è collegata ad altre entità"));
            }
            if ((res as IErrorResponse)?.type === "validation") {
                return setModalError(ValidationError({ error: (res as IValidationErrorResponse) }));
            }
        }
        if (res?.success) {
            setModalAlertMessage(createSuccessMessage("Operazione completata!", "L'operazione è stata completata con successo"));
            await fetchRides();
        } else {
            setModalError(createErrorMessage("Oops!", "Si è verificato un errore durante l'operazione"));
            //setAlertMessage(createErrorMessage("Oops!", "Si è verificato un errore durante l'operazione"));
        }
        return res;
    }

    const modalActions: Actions<Service> = {
        save: {
            label: "Salva",
            onClick: async (entity: Service) => {
                return (!!((await actionAndUpdateView("save", entity))?.success));
            }
        },
        destroy: {
            label: "Elimina",
            onClick: async (entity: Service) => {
                if (!!((await actionAndUpdateView("delete", entity))?.success)) {
                    setAlertMessage(createSuccessMessage("Operazione completata!", "L'operazione è stata completata con successo"));
                    setModalEntity(emptyService);
                    setShowModal(false);
                    return true;
                }
                return false;
            }
        },
        cancel: {
            label: "Chiudi",
            onClick: async (entity: Service) => {
                setModalEntity(emptyService);
                setShowModal(false);
                return true;
            }
        },
        duplicate: {
            label: "Duplica",
            onClick: async (entity: Service) => {
                //TODO: implement
                return true;
            }
        },
        reload: {
            label: "Ricarica",
            onClick: async (entity: Service) => {
                //TODO: implement
                return true;
            }
        }

    }

    const events: EventSourceInput = React.useMemo(() => {
        return rides
            .filter((ride: Ride) => {
                if (filteredDrivers.length > 0 && !filteredDrivers.find((rideID: number) => ride?.driver?.id === rideID)) {
                    return false;
                }
                if (filteredMeans.length > 0 && !filteredMeans.find((meanID: number) => ride?.mean?.id === meanID)) {
                    return false;
                }
                return true;
            })
            .map((ride: Ride) => {
                return {
                    title: `${ride?.driver?.name ?? ""}  ${ride?.driver?.surname ?? ""} <br/>
                    ${ride?.mean?.code ?? ""}  ${ride?.mean?.license_plate ?? ""} <br/>
                    ${ride?.itinerary} `,
                    start: moment(ride.start_departure_date, DATE_TIME_FORMAT).toDate(),
                    end: moment(ride.end_departure_date, DATE_TIME_FORMAT).toDate(),
                    id: (ride.id).toString(),
                    color: getStatusColor(ride)
                }
            });
    }, [rides, filteredDrivers, filteredMeans]);


    const handleDateChange = (arg: DatesSetArg) => {
        if (arg.startStr === dateRange?.startDate && arg.endStr === dateRange?.endDate) {
            return;
        }
        setDateRange({
            startDate: arg.startStr,
            endDate: arg.endStr
        });
    }

    const fetchRides = async (QueryParams?: QueryParams) => {
        const rides = (await RideApi.getAllNoPagination(QueryParams))
        setRides(rides?.data ?? [])
    };

    React.useEffect(() => {
        let filters = {};
        let orders = {};
        if (!dateRange) {
            return;
        }
        filters = {
            "start_departure_date[gte]": dateRange.startDate,
            "end_departure_date[lte]": dateRange.endDate
        }
        fetchRides({filters, orders});
    }, [dateRange]);

    const entityIcon = "fa-solid fa-clipboard-list";

    return (
        <>
            {
                showModal &&
                <ModalEntity<Service>
                    key={modalEntity?.id}
                    title={{
                        icon: entityIcon,
                        label: `Scheda ${modalEntity?.title ?? "Servizio"}`
                    }}
                    actions={modalActions}
                    eform={ServiceFormBuilder({ defaultActiveTab: 1 })}
                    show={showModal}
                    initialEntity={modalEntity}
                    error={modalError}
                    message={modalAlertMessage}
                />
            }
            <ToolbarWrapper
                items={[
                    {
                        display: false,
                        component: {
                            type: 'button',
                            label: `Filtra`,
                            icon: 'fa-solid fa-plus',
                            //onClick: () => setShowCreateModal(true)
                        }
                    },
                    {
                        display: true,
                        component: {
                            type: 'custom',
                            key: "driversFilter",
                            render: () =>
                                <>
                                    <label className="form-label">Filtra per autista</label>
                                    <Select
                                        key="driversFilter"
                                        closeMenuOnSelect={false}
                                        isMulti
                                        options={drivers?.map((driver: Driver) => ({
                                            key: driver?.id,
                                            value: driver?.id,
                                            label: `${driver?.name ?? ""} ${driver?.surname ?? ""}`
                                        }))}
                                        onChange={(e) => {
                                            setFilteredDrivers(e?.map((e: { key: number }) => e?.key) ?? []);
                                        }}
                                    />
                                </>
                        },
                    },
                    {
                        display: true,
                        component: {
                            type: 'custom',
                            key: "meansFilter",
                            render: () =>
                                <>
                                    <label className="form-label">Filtra per mezzo</label>
                                    <Select
                                        key="meansFilter"
                                        closeMenuOnSelect={false}
                                        isMulti
                                        options={means?.map((mean: Mean) => ({
                                            key: mean?.id,
                                            value: mean?.id,
                                            label: `${mean?.code ?? ""} ${mean?.license_plate ?? ""}`
                                        }))}
                                        onChange={(e) => {
                                            setFilteredMeans(e?.map((e: { key: number }) => e?.key) ?? []);
                                        }}
                                    />
                                </>
                        },
                    },
                ]}
            />
            {
                alertMessage
            }
            <div className={'app-container'}>
                {/* begin::Row */}
                <div className='row g-5 g-xl-10 mb-5 mb-xl-10'>
                    {/* begin::Col */}
                    <div className='col-xxl-12'>
                        <div className="card card-custom card-flush">
                            <div className="card-body">
                                <FullCalendar
                                    datesSet={handleDateChange}
                                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                                    locale={intl.locale}
                                    headerToolbar={{
                                        left: "prev,next,today",
                                        center: "title",
                                        right: ""
                                    }}
                                    firstDay={1}
                                    initialView="dayGridMonth"
                                    events={events}
                                    eventContent={(evntInfo) => (
                                        <div className="d-flex align-items-center">
                                            <span className="bullet bullet-bar bg-success align-self-stretch" style={{ width: "4px" }}></span>
                                            {
                                                // TODO: add safety check to the "dangerouslySetInnerHTML"
                                            }
                                            <span className="card-title font-weight-bolder text-dark-75 font-size-h6 mb-0 ml-3" dangerouslySetInnerHTML={{ __html: evntInfo?.event?.title ?? "" }}></span>
                                        </div>
                                    )}
                                    eventClick={async (info: EventClickArg) => {
                                        const entityID = parseInt(info?.event?.id);
                                        const actionEntity = (await ServiceApi.getFullByRideId(entityID, {}))?.data ?? emptyService;
                                        setModalEntity(actionEntity);
                                        setShowModal(true);
                                    }}

                                />
                            </div>
                        </div>

                    </div>
                    {/* end::Col */}
                </div>
                {/* end::Row */}
            </div>
        </>
    );
};

const RidesCalendarWrapper: FC = () => {
    return (
        <>
            <RidesCalendarPage />
        </>
    )
};

export { RidesCalendarWrapper }
